import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, Container, Accordion } from 'react-bootstrap';
import ListGroup from 'react-bootstrap/ListGroup';
import Tab from 'react-bootstrap/Tab';
import CustomAccordion from '../UI/CustomAccordion';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import classes from './Test.module.css';
import { useLocation } from 'react-router-dom';

const ServicesSection = ({ services = [], options, handleClick }) => {
  const location = useLocation();
  const accordionRef = useRef(null);
  const [defaultKey, setDefaultKey] = useState("0");
  
  // derive first slug safely
  const deriveSlug = (svc, idx) => (svc?.Slug || svc?.slug || `service-${idx}`);
  const initial = services.length > 0 ? `#${deriveSlug(services[0], 0)}` : '#no-service';
  const [activeKey, setActiveKey] = useState(initial);

  // Handle search navigation to specific service
  useEffect(() => {
    if (services.length === 0) return;
    
    // Check if we came from search with a specific service index
    if (location.state?.section === 'services' && location.state?.index !== undefined) {
      const targetIndex = location.state.index;
      const targetService = services[targetIndex];
      
      if (targetService) {
        const targetSlug = deriveSlug(targetService, targetIndex);
        setActiveKey(`#${targetSlug}`);
        
        // For mobile accordion - set the default key and trigger re-render
        if (window.innerWidth < 768) {
          // Update the defaultKey to open the target accordion
          setDefaultKey(targetIndex.toString());
          
          // Force accordion to open by clicking the button after a delay
          setTimeout(() => {
            // Find all accordion buttons
            const accordionButtons = document.querySelectorAll('.accordion-custom .accordion-button');
            
            // Close all accordions first
            accordionButtons.forEach((btn, idx) => {
              if (idx !== targetIndex && !btn.classList.contains('collapsed')) {
                btn.click();
              }
            });
            
            // Open the target accordion
            const targetButton = accordionButtons[targetIndex];
            if (targetButton && targetButton.classList.contains('collapsed')) {
              targetButton.click();
            }
          }, 200);
        }
        
        // Scroll to services section
        setTimeout(() => {
          const servicesElement = document.getElementById('services');
          if (servicesElement) {
            servicesElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
    } else if (!location.state) {
      // Only reset to first service if there's no location state at all
      const firstKey = `#${deriveSlug(services[0], 0)}`;
      setActiveKey(firstKey);
      setDefaultKey("0");
    }
  }, [services, location.state]);

  const handleAccordionClick = (slug) => {
    setActiveKey(`#${slug}`);
  };

  return (
    <div id="services">
      <section className={`${classes.main} ${classes.service} ${classes.pb20} ${classes.p60} services-comp d-none d-md-block`}>
        <Container>
          <Row className={`${classes.pb20} text-center`}>
            <Col md={12}>
              <div className={`${classes.headingTagNew} ${classes.headingTagService}`}>Our Services</div>
            </Col>
          </Row>
          <Row>
          {/* {  console.log(services,"here are the services")} */}
            <Tab.Container
              id="list-group-tabs-example"
              activeKey={activeKey}
              onSelect={(k) => k && setActiveKey(k)}
            >
              <Col md={6} className="pb-3">
                <CustomAccordion
                  services={services}
                  options={options}
                  onAccordionClick={handleAccordionClick}
                  activeKey={activeKey}
                />
              </Col>
              <Col md={6} className="pb-3">
                <Tab.Content className={`${classes.card}`}>
                  {services.map((item, id) => {
                    const slug = deriveSlug(item, id);
                    return (
                      <Tab.Pane key={slug} eventKey={`#${slug}`}>
                        <article>
                          <h3 className={classes.cardSecBServicetitle + ' mb-4'}>{item?.Title}</h3>
                          <div>
                            {item?.Desc?.map((info, idx) => (
                              <span key={idx}>{documentToReactComponents(info, options)}</span>
                            ))}
                          </div>
                        </article>
                      </Tab.Pane>
                    );
                  })}
                  {services.length === 0 && (
                    <div className="text-muted p-3">No services available.</div>
                  )}
                </Tab.Content>
              </Col>
            </Tab.Container>
          </Row>
        </Container>
      </section>

      <section className={`${classes.main} ${classes.service} ${classes.pb20} ${classes.p40} services-comp d-block d-md-none`}>
        <Container>
          <Row className={`${classes.pb20} text-center`}>
            <Col md={12}>
              <div className={`${classes.headingTagNew} ${classes.headingTagService}`}>Our Services</div>
            </Col>
          </Row>
          <Row>
            <Col className="pb-3">
              <ListGroup className={classes.serviceList + " services-list"}>
                <Accordion 
                  ref={accordionRef}
                  className="accordion-custom" 
                  defaultActiveKey={defaultKey}
                  key={defaultKey} // Force re-render when defaultKey changes
                >
                  {services.map((item, id) => {
                    const slug = deriveSlug(item, id);
                    
                    return (
                      <ListGroup.Item key={id} action href={`#${slug}`}>
                        <Accordion.Item eventKey={id.toString()} className={classes.faQQ}>
                          <Accordion.Header 
                            onClick={() => handleClick && handleClick(id)} 
                            className="service-header"
                            data-index={id}
                          >
                            <span className={classes.serviceIcon}>
                              <img src={item.Icon} className="services" alt={item?.Title || 'Service'} />
                            </span>
                            {item.Title}
                          </Accordion.Header>
                          <Accordion.Body>
                            <article className="pl-3 pb-3">
                              <div className={classes.cardImageService}>
                                <img src={item.Image} alt={item?.Title || 'Service image'} className={classes.cardImageService} width="480" height="719" />
                              </div>
                              <article className={classes.cardServiceSecB + ' pl-3'} style={{ paddingLeft: '10px' }}>
                                <div className={classes.cardServiceText + ' pl-2 mt-3'}>
                                  {item?.Desc?.map((info, idx) => (
                                    <span key={idx}>{documentToReactComponents(info, options)}</span>
                                  ))}
                                </div>
                              </article>
                            </article>
                          </Accordion.Body>
                        </Accordion.Item>
                      </ListGroup.Item>
                    );
                  })}
                  {services.length === 0 && <div className="text-muted p-3">No services available.</div>}
                </Accordion>
              </ListGroup>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default ServicesSection;